import type { DashboardId, Visit } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, bars, chip, list, text, type Rect } from '../charts.ts';
import { visitDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

const TIMING_COLOUR: Record<Visit['timing'], string> = {
  'On time': PALETTE.teal,
  Early: PALETTE.violet,
  Late: PALETTE.amber,
  Missed: PALETTE.crimson,
  Pending: PALETTE.idle,
};

/** Visit Compliance: on-time/early/late/missed, Gantt-style timeline per subject, bars per site. */
export class VisitComplianceDashboard extends Dashboard {
  readonly id: DashboardId = 'VisitCompliance';
  private page = 0;
  private readonly pageSize = 12;

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('visit:'))
      return this.view.visit(segment.slice(6))?.visitName ?? segment;
    return segment.replace(/^subject:/, '');
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [subject, visit] = this.path;
    if (subject && visit) {
      const v = this.view.visit(visit.slice(6));
      if (v) {
        this.detail = visitDetail(this.view, v);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    if (subject) {
      const id = subject.slice(8);
      const visits = this.view.file.visits.filter((v) => v.subjectId === id);
      const rows = list(
        ctx,
        ['Visit', 'Target', 'Window', 'Actual', 'Timing', 'Reason'],
        visits.map((v) => ({
          cells: [
            v.visitName,
            v.targetDate,
            `${v.windowStart.slice(5)} – ${v.windowEnd.slice(5)}`,
            v.actualDate ?? '—',
            v.timing,
            v.missedReason ?? '',
          ],
          flag:
            v.timing === 'Late' || v.timing === 'Early' || v.status === 'Missed' ? 'x' : undefined,
          colour: TIMING_COLOUR[v.timing],
        })),
        area,
        {
          title: `${id} · ${visits.length} scheduled visits`,
          widths: [0.2, 0.14, 0.2, 0.14, 0.12, 0.2],
          hoveredIndex: hoveredRow(panel, 'row:'),
          rowH: 20,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `visit:${visits[i]!.id}` },
          tooltip: [visits[i]!.visitName, `${visits[i]!.status} · ${visits[i]!.timing}`],
        }),
      );
      return;
    }
    const due = this.view
      .visits(this.filters)
      .filter((v) => v.status === 'Completed' || v.status === 'Missed');
    const count = (t: Visit['timing']) => due.filter((v) => v.timing === t).length;
    const pct = (n: number) => (due.length ? `${Math.round((n / due.length) * 1000) / 10}%` : '—');
    const devs = due
      .filter((v) => v.status === 'Completed')
      .map((v) => Math.abs(v.deviationDays ?? 0));
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'On time',
          value: pct(count('On time')),
          colour: PALETTE.teal,
          sub: `${count('On time')} of ${due.length} due`,
        },
        { label: 'Early', value: pct(count('Early')), colour: PALETTE.violet },
        { label: 'Late', value: pct(count('Late')), colour: PALETTE.amber },
        { label: 'Missed', value: pct(count('Missed')), colour: PALETTE.crimson },
        {
          label: 'Mean deviation',
          value: devs.length
            ? `${(devs.reduce((a, b) => a + b, 0) / devs.length).toFixed(1)} d`
            : '—',
        },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    // Gantt: one row per subject, one block per scheduled visit coloured by timing.
    const gantt: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.62, h: area.h - 88 };
    const subjects = this.view.subjects(this.filters).filter((s) => s.firstDoseDate);
    const pages = Math.max(1, Math.ceil(subjects.length / this.pageSize));
    this.page = Math.min(this.page, pages - 1);
    const pageSubjects = subjects.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize);
    text(ctx, `Visit timeline per subject (${this.page + 1}/${pages})`, gantt.x, gantt.y + 12, {
      size: 12,
      colour: PALETTE.muted,
      weight: 600,
    });
    const prev = chip(ctx, '‹', gantt.x + gantt.w - 58, gantt.y, {
      h: 18,
      size: 10,
      hovered: panel.isHovered('page:prev'),
    });
    const next = chip(ctx, '›', gantt.x + gantt.w - 26, gantt.y, {
      h: 18,
      size: 10,
      hovered: panel.isHovered('page:next'),
    });
    panel.region<ViewRegion>({ id: 'page:prev', ...prev, data: { kind: 'info' } });
    panel.region<ViewRegion>({ id: 'page:next', ...next, data: { kind: 'info' } });
    const schedule = this.view.file.study.visitSchedule;
    const labelW = 70;
    const rowH = Math.min(22, (gantt.h - 24) / Math.max(1, pageSubjects.length));
    const cellW = (gantt.w - labelW) / schedule.length;
    pageSubjects.forEach((s, ri) => {
      const y = gantt.y + 22 + ri * rowH;
      const rowRect = { x: gantt.x, y, w: labelW, h: rowH };
      if (panel.isHovered(`subject:${ri}`)) {
        ctx.fillStyle = PALETTE.hover;
        ctx.fillRect(gantt.x, y, gantt.w, rowH);
      }
      text(ctx, s.id, gantt.x + 2, y + rowH / 2 + 4, { size: 10 });
      panel.region<ViewRegion>({
        id: `subject:${ri}`,
        ...rowRect,
        data: { kind: 'drill', segment: `subject:${s.id}` },
        tooltip: [s.id, 'Open the visit list'],
      });
      const visits = this.view.file.visits.filter((v) => v.subjectId === s.id);
      visits.forEach((v) => {
        const ci = this.view.visitIndex(v.visitCode);
        if (ci < 0) return;
        const r = { x: gantt.x + labelW + ci * cellW + 1, y: y + 3, w: cellW - 2, h: rowH - 6 };
        ctx.fillStyle = v.status === 'Not Applicable' ? PALETTE.faint : TIMING_COLOUR[v.timing];
        ctx.fillRect(r.x, r.y, r.w, r.h);
        if (v.status !== 'Not Applicable') {
          panel.region<ViewRegion>({
            id: `cell:${ri}:${ci}`,
            ...r,
            data: { kind: 'drill', segment: `subject:${s.id}` },
            tooltip: [
              `${s.id} · ${v.visitName}`,
              v.actualDate ? `${v.actualDate} · ${v.timing} (${v.deviationDays ?? 0} d)` : v.status,
            ],
          });
        }
      });
    });
    schedule.forEach((v, ci) => {
      if (ci % Math.ceil(schedule.length / 8) === 0 || ci === schedule.length - 1)
        text(ctx, v.code, gantt.x + labelW + ci * cellW + cellW / 2, gantt.y + gantt.h - 2, {
          size: 9,
          colour: PALETTE.muted,
          align: 'center',
        });
    });
    // Per-site bars.
    const right: Rect = {
      x: area.x + area.w * 0.66,
      y: area.y + 88,
      w: area.w * 0.34,
      h: area.h - 88,
    };
    const sites = this.view.file.sites;
    const data = sites.map((site) => {
      const vs = due.filter((v) => v.siteId === site.id);
      return {
        label: site.city,
        values: [
          vs.filter((v) => v.timing === 'On time').length,
          vs.filter((v) => v.timing === 'Early').length,
          vs.filter((v) => v.timing === 'Late').length,
          vs.filter((v) => v.timing === 'Missed').length,
        ],
        colours: [PALETTE.teal, PALETTE.violet, PALETTE.amber, PALETTE.crimson],
      };
    });
    const rects = bars(ctx, data, right, {
      stacked: true,
      title: 'Compliance per site',
      hoveredIndex: hoveredRow(panel, 'site:'),
    });
    rects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [
          this.view.siteLabel(sites[i]!.id),
          `on time ${data[i]!.values[0]} · early ${data[i]!.values[1]} · late ${data[i]!.values[2]} · missed ${data[i]!.values[3]}`,
        ],
        selectable: false,
      }),
    );
  }

  protected override onInfo(regionId: string): void {
    if (regionId === 'page:prev') this.nextPage(-1);
    else if (regionId === 'page:next') this.nextPage(1);
  }

  nextPage(delta: number): void {
    this.page = Math.max(0, this.page + delta);
    this.invalidate();
  }
}
